import React, { useEffect, useMemo, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { ChannelPageHero, ChannelEmpty } from '@/pages/WatchPage';
import { useGlobalAudio } from '@/components/GlobalAudioPlayer';
import { Play, Pause, Music as MusicIcon, ListMusic, Shuffle } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * MusicPage — Phase 17 Streaming Experience
 * -----------------------------------------
 * Every track is a first-class content card. Layout emphasizes cover artwork
 * and title with a single-click Play Now button. A Play All button above the
 * grid switches the global player into Playlist Mode. Currently playing card
 * is highlighted and its cover art is synchronized as the "Now Playing" hero
 * artwork automatically (via the shared player state).
 */
export default function MusicPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    current, isPlaying, togglePlay, playTrack, playAll,
    shuffle, setShuffle, playlistMode,
  } = useGlobalAudio();

  useEffect(() => {
    api.get('/audio').then(({ data }) => {
      const music = (data || []).filter((a) => a.kind === 'music' || a.kind === 'reflection');
      setItems(music);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const playable = useMemo(
    () => items.filter((it) => it.file_url || (it.source_type === 'embed' && it.embed_url)),
    [items]
  );

  // Play Now — single-track mode: play just this song via playTrack.
  const handlePlay = (track) => {
    if (current?.id === track.id) { togglePlay(); return; }
    playTrack(track);
  };

  // Play All — enter Playlist Mode using all playable tracks. If a track is
  // already playing that belongs to the list, the player preserves position.
  const handlePlayAll = () => {
    if (playable.length === 0) return;
    playAll(playable);
  };

  const handleShuffleAll = () => {
    if (playable.length === 0) return;
    if (!shuffle) setShuffle(true);
    playAll(playable);
  };

  return (
    <div data-testid="music-page">
      <ChannelPageHero
        kicker="CHANNEL — MUSIC"
        title="MUSIC"
        blurb="Tracks, albums, singles, and audio reflections. Every song is its own release."
        accent="emerald"
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-32">
        {loading ? (
          <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Cueing the queue…</div>
        ) : items.length === 0 ? (
          <ChannelEmpty title="THE DECK IS WARM" copy="Tracks will land here. Subscribe to be the first to listen." />
        ) : (
          <>
            <MusicHeader
              count={playable.length}
              onPlayAll={handlePlayAll}
              onShuffleAll={handleShuffleAll}
              playlistMode={playlistMode}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5" data-testid="music-grid">
              {items.map((it) => {
                const active = current?.id === it.id;
                const showPlay = !active || !isPlaying;
                const disabled = !(it.file_url || (it.source_type === 'embed' && it.embed_url));
                return (
                  <MusicCard
                    key={it.id}
                    track={it}
                    active={active}
                    showPlay={showPlay}
                    disabled={disabled}
                    onPlay={() => !disabled && handlePlay(it)}
                    isPlaying={isPlaying && active}
                  />
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function MusicHeader({ count, onPlayAll, onShuffleAll, playlistMode }) {
  return (
    <div className="pg-card p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="music-header">
      <div className="min-w-0">
        <p className="pg-label text-[10px]">MUSIC LIBRARY</p>
        <p className="text-[11px] font-mono tracking-widest text-[var(--pg-ink-faint)] mt-1">
          {count} SONG{count === 1 ? '' : 'S'}{playlistMode ? ' · PLAYLIST MODE' : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onShuffleAll}
          data-testid="music-shuffle-all"
          className="pg-btn-ghost rounded-[var(--pg-radius-md)] px-3 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"
        >
          <Shuffle size={13} /> SHUFFLE ALL
        </button>
        <button
          onClick={onPlayAll}
          data-testid="music-play-all"
          className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"
        >
          <ListMusic size={13} /> PLAY ALL
        </button>
      </div>
    </div>
  );
}

function MusicCard({ track, active, showPlay, disabled, onPlay, isPlaying }) {
  return (
    <motion.div
      initial={{ opacity: 1, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={`group pg-card overflow-hidden transition-colors ${active ? 'border-[var(--pg-emerald)]' : 'hover:border-[rgba(25,194,142,0.4)]'}`}
      style={{ boxShadow: active ? '0 12px 40px rgba(0,0,0,0.55), 0 0 24px rgba(25,194,142,0.20)' : undefined }}
      data-testid="music-card"
      data-active={active ? '1' : '0'}
    >
      <div className="relative aspect-square bg-[var(--pg-navy-800)] overflow-hidden">
        {track.cover_url ? (
          <img src={mediaUrl(track.cover_url)} alt={track.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><MusicIcon size={48} /></div>
        )}
        {/* Play/Pause overlay button */}
        <button
          onClick={onPlay}
          disabled={disabled}
          data-testid="music-card-play"
          aria-label={showPlay ? `Play ${track.title}` : `Pause ${track.title}`}
          className={`absolute inset-0 flex items-center justify-center transition-opacity ${
            disabled
              ? 'opacity-40 cursor-not-allowed'
              : active
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100'
          }`}
          style={{ background: active ? 'linear-gradient(180deg, rgba(4,6,12,0.10), rgba(4,6,12,0.55))' : 'rgba(4,6,12,0.35)' }}
        >
          <span className={`h-14 w-14 rounded-full flex items-center justify-center shadow-[0_6px_20px_rgba(14,107,85,0.35)] ${disabled ? 'bg-[var(--pg-navy-800)] text-[var(--pg-ink-faint)]' : 'bg-[var(--pg-emerald)] text-[var(--pg-ink)]'}`}>
            {showPlay ? <Play size={20} className="ml-1" /> : <Pause size={20} />}
          </span>
        </button>
        {/* Now-playing indicator */}
        {active && isPlaying && (
          <span className="absolute top-3 left-3 inline-flex items-end gap-[2px] h-4 rounded-full bg-[rgba(4,6,12,0.55)] px-2 py-1" aria-hidden>
            <span className="pg-bar" />
            <span className="pg-bar" style={{ animationDelay: '0.2s' }} />
            <span className="pg-bar" style={{ animationDelay: '0.4s' }} />
          </span>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <p className={`font-serif text-sm sm:text-base line-clamp-2 leading-snug ${active ? 'pg-text-glow-emerald' : 'text-[var(--pg-ink)]'}`}>
          {track.title}
        </p>
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase mt-1 truncate">
          {track.kind || 'music'}{track.duration ? ` · ${track.duration}` : ''}
        </p>
      </div>
    </motion.div>
  );
}
